import React, { useState, useEffect, useRef } from 'react';
import {
  X, Play, Pause, Volume2, VolumeX, Clock, Phone, PhoneIncoming,
  PhoneOutgoing, User, Bot, MessageSquare, Download, SkipBack, SkipForward,
  Loader2, AlertCircle
} from 'lucide-react';
import { callPlaybackAPI } from '../services/api';
import TransferDiagnosticsPanel from './call-details/TransferDiagnosticsPanel';

export default function CallPlaybackModal({ callId, onClose, clientId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [callData, setCallData] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchCallData();
  }, [callId]);

  const fetchCallData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await callPlaybackAPI.getTranscript(callId, clientId);
      setCallData(response.data);
    } catch (err) {
      console.error('Error fetching call data:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load call data');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = percent * duration;
  };

  const handleSkip = (seconds) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(0, Math.min(duration, audioRef.current.currentTime + seconds));
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="bg-[#0d1117] border border-gray-800 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="call-playback-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              callData?.direction === 'inbound' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'
            }`}>
              {callData?.direction === 'inbound' ? <PhoneIncoming className="w-5 h-5" /> : <PhoneOutgoing className="w-5 h-5" />}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Call Recording</h2>
              <p className="text-gray-400 text-sm">
                {callData?.direction === 'inbound' ? callData?.from_number : callData?.to_number}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            data-testid="close-modal-btn"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-200px)]">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
              <p className="text-red-400 mb-4">{error}</p>
              <button
                onClick={fetchCallData}
                className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              {/* Call Details */}
              <div className="p-4 border-b border-gray-800">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Date & Time</p>
                    <p className="text-white">{formatDateTime(callData?.started_at)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Duration</p>
                    <p className="text-white">{formatTime(callData?.duration_seconds)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Status</p>
                    <p className={`capitalize ${
                      callData?.status === 'completed' ? 'text-green-400' : 'text-gray-400'
                    }`}>{callData?.status}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">AI Agent</p>
                    <p className="text-teal-400">{callData?.agent_name || 'N/A'}</p>
                  </div>
                </div>
                {callData?.campaign_name && (
                  <div className="mt-3 px-3 py-2 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                    <p className="text-orange-400 text-sm">Campaign: {callData.campaign_name}</p>
                  </div>
                )}
              </div>

              {/* Audio Player */}
              {callData?.has_recording && callData?.recording_url ? (
                <div className="p-4 border-b border-gray-800 bg-gray-900/50">
                  <audio
                    ref={audioRef}
                    src={callData.recording_url}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    onEnded={handleEnded}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                  />
                  
                  {/* Progress Bar */}
                  <div 
                    className="h-2 bg-gray-700 rounded-full cursor-pointer mb-4 group"
                    onClick={handleSeek}
                  >
                    <div 
                      className="h-full bg-gradient-to-r from-teal-500 to-teal-400 rounded-full relative"
                      style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                    >
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    </div>
                  </div>
                  
                  {/* Controls */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleSkip(-10)}
                        className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                        title="Back 10s"
                      >
                        <SkipBack className="w-5 h-5 text-gray-400" />
                      </button>
                      
                      <button
                        onClick={handlePlayPause}
                        className="w-12 h-12 bg-teal-500 hover:bg-teal-400 rounded-full flex items-center justify-center transition-colors"
                        data-testid="play-pause-btn"
                      >
                        {isPlaying ? (
                          <Pause className="w-5 h-5 text-white" />
                        ) : (
                          <Play className="w-5 h-5 text-white ml-0.5" />
                        )}
                      </button>
                      
                      <button
                        onClick={() => handleSkip(10)}
                        className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                        title="Forward 10s"
                      >
                        <SkipForward className="w-5 h-5 text-gray-400" />
                      </button>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <span className="text-gray-400 text-sm font-mono">
                        {formatTime(currentTime)} / {formatTime(duration)}
                      </span>
                      
                      <button
                        onClick={toggleMute}
                        className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        {isMuted ? (
                          <VolumeX className="w-5 h-5 text-gray-400" />
                        ) : (
                          <Volume2 className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                      
                      <a
                        href={callData.recording_url}
                        download
                        className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                        title="Download Recording"
                      >
                        <Download className="w-5 h-5 text-gray-400" />
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 border-b border-gray-800 bg-gray-900/50 text-center">
                  <p className="text-gray-500 text-sm">No recording available for this call</p>
                </div>
              )}

              {/* Conversation Transcript */}
              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-400 mb-4 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Conversation Transcript ({callData?.conversation?.length || 0} messages)
                </h3>
                
                {callData?.conversation && callData.conversation.length > 0 ? (
                  <div className="space-y-3">
                    {callData.conversation.map((message, index) => {
                      const isAI = message.role === 'assistant' || message.role === 'ai' || message.role === 'agent';
                      return (
                        <div
                          key={index}
                          className={`flex gap-3 ${isAI ? '' : 'flex-row-reverse'}`}
                          data-testid={`message-${index}`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isAI ? 'bg-teal-500/20 text-teal-400' : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {isAI ? <Bot className="w-4 h-4" /> : <User className="w-4 h-4" />}
                          </div>
                          <div className={`max-w-[80%] ${isAI ? '' : 'text-right'}`}>
                            <p className="text-xs text-gray-500 mb-1 capitalize">
                              {isAI ? 'AI Agent' : 'Customer'}
                            </p>
                            <div className={`px-4 py-2 rounded-2xl ${
                              isAI 
                                ? 'bg-gray-800 text-white rounded-tl-sm' 
                                : 'bg-blue-500/20 text-blue-100 rounded-tr-sm'
                            }`}>
                              <p className="text-sm">{message.content}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <MessageSquare className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                    <p className="text-gray-500">No transcript available for this call</p>
                  </div>
                )}
              </div>

              {/* Transfer Diagnostics */}
              <TransferDiagnosticsPanel callData={callData} />
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-800 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
